/**
* TextWrap.js
* 
* Created on 16-okt-2014, 9:16:42
*
* @author Harm Boschloo for Ranj
* @author Henrique Campos
**/

import { containsCJKCharacters } from "../../util/StringUtil";

export default class TextWrap {
    /**
     * Applies forceful word wrapping to a text by splitting lines that do not fit the given width.
     * 
     * Note: this wrapping approach is destructive as it adds the provided wrap separator string to the text in
     * order to force word wrapping.
     * 
     * @param {string} text the string to wrap
     * @param {number} maxWidth maximum width of each line in pixels
     * @param {CanvasRenderingContext2D} context the Canvas rendering context to determine the text metrics
     * @param {string} wrapSeparator the separator to use when a line is wrapped
     * @returns {string} text after wrapping
     */
    static wrapText(text, maxWidth, context, wrapSeparator = ' ') {
        // first, split text by newlines
        const originalLines = text.split(/(?:\r\n|\r|\n)/);
        const newLines = [];

        for (let line of originalLines) {
            const lineWidth = context.measureText(line).width;
            if (lineWidth <= maxWidth) {
                newLines.push(line);
            } else {
                const wrappedLine = TextWrap._wrapLine(line, maxWidth, context, wrapSeparator);
                newLines.push(wrappedLine);
            }
        }
        return newLines.join('\n');
    }

    /**
     * Applies forceful word wrapping to a createjs.Text by splitting lines that do not fit the given width.
     * 
     * Note: this approach is useful as createjs.Text only word wraps based on white-spaces.
     * 
     * Note: this wrapping approach is destructive as it adds the provided wrap separator string to the text in
     * order to force word wrapping.
     * 
     * @param {createjs.Text} textField the createjs.Text to wrap
     * @param {number} lineWidth the target line width in pixels; if none is provided it uses the createjs.Text.lineWidth
     */
    static wrapTextField(textField, lineWidth = undefined, wrapSeparator = ' ') {
        // NB: if no line width is provided, try the Text lineWidth
        lineWidth = lineWidth || textField.lineWidth;
        if (!lineWidth) {
            console.error("lineWidth or textField.lineWidth not provided");
            return;
        }

        // NB: apply the CanvasRenderingContext2D to the TextField to determine size of text
        /** @type {CanvasRenderingContext2D} */
        const context = textField._prepContext(createjs.Text._workingContext);
        textField.text = TextWrap.wrapText(textField.text, lineWidth, context, wrapSeparator);
    }

    /**
     * 
     * @param {string} line 
     * @param {number} maxWidth 
     * @param {CanvasRenderingContext2D} context 
     * @return {string}
     */
    static _wrapLine(line, maxWidth, context, wrapSeparator = ' ') {
        // split words on any white space character; returns white spaces as well
        const parts = line.split(/(\s)/);

        const splitLines = [];
        let newLine = "";
        for (let i = 0; i < parts.length; i++) {
            const part = parts[i];
            const newLineWidth = context.measureText(newLine + part).width;

            if (newLineWidth <= maxWidth) {
                // if text fits, just add it to the new line
                newLine += part;
            } else if (containsCJKCharacters(part)) {
                // split part with latin words and numbers as these should wrapped separately. Ensure there is at least one letter or digit (prevents wrapping of latin punctuation-only parts)
                // NB: this covers the use case of latin words between CJK text. Even in this case, no white spaces are normally used.
                // NB: this only covers latin alphabet, and it does not cover uncommon cases of CJK mixed with other alphabets (e.g., cyrillic)
                const splitCJKLatin = part.split(/([a-z|A-Z|0-9|,.]*[a-z|A-Z|0-9]+[a-z|A-Z|0-9|,.]*)/);
                // if part splits between CJK and latin/numbers, then add these to the parts and process them again
                if (splitCJKLatin.length > 1) {
                    parts.splice(i, 1, ...splitCJKLatin);
                    i--;
                } else {
                    // if part has CJK characters, ensure that the string is split following proper wrapping rules
                    const splitWord = TextWrap._splitString(newLine + part, maxWidth, context, true);
                    newLine = splitWord[0];
                    // add remainder to the parts so it is processed again
                    parts.splice(i + 1, 0, splitWord[1]);
                    splitLines.push(newLine);
                    newLine = "";
                }
            } else if (newLine === "") {
                // if part is too big to even fit by itself in one line, split it
                const wordParts = TextWrap._splitString(part, maxWidth, context);
                // add split word into the parts by replacing its original so the remainder is processed next
                parts.splice(i, 1, ...wordParts);
                // add first part of the word to the lines
                splitLines.push(wordParts[0]);
            } else {
                // if current part does not fit in the new line, then add new line to lines array
                splitLines.push(newLine);
                newLine = "";
                // if current part that wraps the line is not any white space, process it again in the next iteration
                // NB: this ensures that a white space that wraps a line is not rendered again, i.e., causing the
                // next line to start with that white space.
                if (!/\s/g.test(part)) {
                    i--;
                }
            }
        }
        splitLines.push(newLine);
        return splitLines.join(wrapSeparator);
    }

    /**
     * Splits a string in two: a width fitting word and the remainder
     * 
     * @param {string} str 
     * @param {number} maxWidth 
     * @param {CanvasRenderingContext2D} context 
     * @param {boolean} isCJK
     * @returns {Array<string>} where the first fits the width and the second is the remainder
     */
    static _splitString(str, maxWidth, context, isCJK = false) {
        let firstPart = str;
        let wrapPosition = str.length;
        // iterates through first part by reducing 1 character at a time until it fits
        while (context.measureText(firstPart).width > maxWidth) {
            wrapPosition--;
            firstPart = str.substring(0, wrapPosition);
        }

        // check if should apply Kinsoku Shori wrap
        if (isCJK && wrapPosition < str.length) {
            let cjkWrapPosition = wrapPosition;
            // While wrap position does not respect Kinsoku Shori wrap rules, decrease the wrap position.
            // NB: In a nutshell, this passes characters from the first part into the remainder until the rule is respected.
            while (cjkWrapPosition > 0 && _isValidKinsokuShoriWrap(str, cjkWrapPosition)) {
                cjkWrapPosition--;
            }
            // if there is a new first part, assign it and determine new first part
            // NB: this covers edge case if a line only has characters that cannot be wrapped
            if (cjkWrapPosition > 0 && cjkWrapPosition != wrapPosition) {
                wrapPosition = cjkWrapPosition;
                firstPart = str.substring(0, wrapPosition);
            }
        }
        const remainder = str.substring(wrapPosition, str.length - wrapPosition);
        // create array where first is the word that fits the width followed by the remainder word
        return [firstPart, remainder];
    }
}

// Line breaking rules in CJK (following Kinsoku Shori as it fits all 4 languages - incl. chinese traditional and simplified)
// Refer to: https://en.wikipedia.org/wiki/Line_breaking_rules_in_East_Asian_languages
// Regex rules based from: https://github.com/huang-yuwei/pixi-cjk/blob/main/src/KinsokuShori.ts
const RegexCannotStartZhCn = /[!%),.:;?\]}¢°·'""†‡›℃∶、。〃〆〕〗〞﹚﹜！＂％＇），．：；？！］｝～]/;
const RegexCannotEndZhCn = /[$(£¥·'"〈《「『【〔〖〝﹙﹛＄（．［｛￡￥]/;
const RegexCannotStartZhTw = /[!),.:;?\]}¢·–—'"•"、。〆〞〕〉》」︰︱︲︳﹐﹑﹒﹓﹔﹕﹖﹘﹚﹜！），．：；？︶︸︺︼︾﹀﹂﹗］｜｝､]/;
const RegexCannotEndZhTw = /[([{£¥'"‵〈《「『〔〝︴﹙﹛（｛︵︷︹︻︽︿﹁﹃﹏]/;
const RegexCannotStartJaJp = /[)\]｝〕〉》」』】〙〗〟'"｠»ヽヾーァィゥェォッャュョヮヵヶぁぃぅぇぉっゃゅょゎゕゖㇰㇱㇲㇳㇴㇵㇶㇷㇸㇹㇺㇻㇼㇽㇾㇿ々〻‐゠–〜?!‼⁇⁈⁉・、:;,。.]/;
const RegexCannotEndJaJp = /[([｛〔〈《「『【〘〖〝'"｟«—...‥〳〴〵]/;
const RegexCannotStartKoKr = /[!%),.:;?\]}¢°'"†‡℃〆〈《「『〕！％），．：；？］｝]/;
const RegexCannotEndKoKr = /[$([{£¥'"々〇〉》」〔＄（［｛｠￥￦#]/;

const RegexCannotStart = new RegExp(
    `${RegexCannotStartZhCn.source}|${RegexCannotStartZhTw.source}|${RegexCannotStartJaJp.source}|${RegexCannotStartKoKr.source}`
);

const RegexCannotEnd = new RegExp(
    `${RegexCannotEndZhCn.source}|${RegexCannotEndZhTw.source}|${RegexCannotEndJaJp.source}|${RegexCannotEndKoKr.source}`
);

const _isValidKinsokuShoriWrap = (str, wrapPosition) => {
    // Check if wrap position respects the Kinsoku Shori wrap rules by checking that:
    // - the first part does not end with a invalid character,
    // - or that the remainder part does not start with an invalid character.
    //
    // NB: Kinsoku Shori allows for words to be broken in any part as long as certain characters do not end or
    // start a line.
    const endCharacterFirstPart = str[wrapPosition - 1];
    const startCharacterRemainder = str[wrapPosition];
    return RegexCannotEnd.exec(endCharacterFirstPart) || RegexCannotStart.exec(startCharacterRemainder);
};
